import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPaperPlane,
    faFileInvoiceDollar,
    faDiagramProject,
    faComment,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/client";
import "./Messages.css";

const TYPE_META = {
    message: { label: "Message", icon: faComment },
    quote_request: { label: "Quote Request", icon: faFileInvoiceDollar },
    project_request: { label: "Project Request", icon: faDiagramProject },
};

export const MessageThread = ({ messages, meId }) => {
    const endRef = useRef(null);

    useEffect(() => {
        endRef.current?.scrollIntoView({ block: "end" });
    }, [messages]);

    if (messages.length === 0) {
        return (
            <p className="messages__empty">
                No messages yet. Send a message, or request a quote or project
                — replies land here and you'll also get an email.
            </p>
        );
    }

    return (
        <div className="messages__thread">
            {messages.map((m) => {
                const mine = m.sender_id === meId;
                const meta = TYPE_META[m.type] || TYPE_META.message;
                return (
                    <div
                        key={m.id}
                        className={`messages__bubble-row ${mine ? "messages__bubble-row--mine" : ""}`}
                    >
                        <div
                            className={`messages__bubble ${mine ? "messages__bubble--mine" : ""}`}
                        >
                            {m.type !== "message" && (
                                <span className="messages__type-tag">
                                    <FontAwesomeIcon icon={meta.icon} />{" "}
                                    {meta.label}
                                </span>
                            )}
                            {m.subject && (
                                <strong className="messages__subject">
                                    {m.subject}
                                </strong>
                            )}
                            <p>{m.body}</p>
                            <span className="messages__meta">
                                {m.sender?.name} ·{" "}
                                {new Date(m.created_at).toLocaleString(
                                    "en-CA",
                                    {
                                        dateStyle: "medium",
                                        timeStyle: "short",
                                    },
                                )}
                            </span>
                        </div>
                    </div>
                );
            })}
            <div ref={endRef} />
        </div>
    );
};

/** Client messages page: own thread + composer with request types. */
const MessagesPage = () => {
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [type, setType] = useState("message");
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");

    const load = useCallback(() => {
        api.messages()
            .then((res) => setMessages(res.messages || []))
            .finally(() => setLoading(false));
        api.markMessagesRead().catch(() => {});
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const send = async (e) => {
        e.preventDefault();
        if (!body.trim()) return;
        setSending(true);
        setError("");
        try {
            const res = await api.sendMessage({
                type,
                subject: subject || null,
                body: body.trim(),
            });
            setMessages((prev) => [...prev, res.message]);
            setBody("");
            setSubject("");
            setType("message");
        } catch (err) {
            setError(err.message);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="messages">
            <div className="section-header">
                <h2>Messages</h2>
            </div>
            <p className="messages__intro">
                Questions, quote requests, or new project ideas — send them
                here. Everything is logged and Kyle is notified by email.
            </p>

            <div className="card messages__panel">
                {loading ? (
                    <p className="messages__empty">Loading…</p>
                ) : (
                    <MessageThread messages={messages} meId={user?.id} />
                )}

                <form className="messages__composer" onSubmit={send}>
                    {error && <div className="error-message">{error}</div>}
                    <div className="messages__composer-row">
                        <select
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                            className="messages__type-select"
                        >
                            <option value="message">General message</option>
                            <option value="quote_request">
                                Request a quote
                            </option>
                            <option value="project_request">
                                Request a project
                            </option>
                        </select>
                        {type !== "message" && (
                            <input
                                className="messages__subject-input"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                placeholder={
                                    type === "quote_request"
                                        ? "What do you need quoted?"
                                        : "Project name / idea"
                                }
                            />
                        )}
                    </div>
                    <div className="messages__composer-row">
                        <textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            placeholder="Write your message…"
                            rows={3}
                            required
                        />
                        <button
                            type="submit"
                            className="btn btn--primary"
                            disabled={sending || !body.trim()}
                        >
                            <FontAwesomeIcon icon={faPaperPlane} />{" "}
                            {sending ? "Sending…" : "Send"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default MessagesPage;
